import DateInput from '../components/DateInput';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doctorProfileAPI } from '../services/api';
import { PageHeader, ActionBtn, Card, Modal } from '../components/Shared';
import toast from 'react-hot-toast';
import { DOCUMENT_TYPES } from '../constants/documentTypes';
import DocumentUploadModal from '../components/DocumentUploadModal';
import DocumentImagePreview from '../components/DocumentImagePreview';
import styles from './Pages.module.css';

import DoctorMonthlyClearingsTab from '../components/DoctorMonthlyClearingsTab'; /* @d10-clearings-import */
import DoctorClinicsTab from '../components/DoctorClinicsTab'; /* @clinic-pairings-import */

// Format any value (number or numeric string from Postgres NUMERIC) as €X.XX.
// Defensive: handles null/undefined/empty string by returning €0.00.
const fmtEuro = (v) => `€${Number(v || 0).toFixed(2)}`;

// DOCUMENT_TYPES moved to src/constants/documentTypes.js — see import above.

// Translates the appointments.status / consultation_outcome value into Greek
// for display in the consultations table. Covers the 3 final states a closed
// consultation can have. Live states (scheduled, in_progress, etc.) fall through
// and display as-is so we don't accidentally hide a state we forgot to translate.
const COMPLETION_STATUS_LABELS_EL = {
  completed: 'Ολοκληρώθηκε',
  no_show: 'DNA',
  abandoned: 'Διακόπηκε',
  scheduled: 'Προγραμματισμένο',
  in_progress: 'Σε εξέλιξη',
  cancelled_by_patient: 'Ακυρωμένο (ασθενής)',
  cancelled_by_doctor: 'Ακυρωμένο (ιατρός)'
};

// Translates the clinical_outcome enum (the doctor's medical decision) into Greek.
// Must match the DB CHECK constraint and backend VALID_CLINICAL_OUTCOMES.
const CLINICAL_OUTCOME_LABELS_EL = {
  prescription: 'Συνταγογράφηση',
  advice_and_reassurance: 'Συμβουλή και Καθησύχαση',
  referral_to_specialist: 'Παραπεμπτικό σε ειδικό',
  referral_for_tests: 'Παραπεμπτικό για εξετάσεις',
  refer_to_own_gp: 'Παραπομπή στον Οικογενειακό Ιατρό',
  fit_note: 'Ιατρική Βεβαίωση'
};

// D7-portal: structured leaver reasons (codes match what we send to backend)
// Backend stores whatever string we send to doctor_profiles.left_reason — no
// CHECK constraint, so the dropdown values are owned by the frontend.
const LEAVER_REASONS = [
  { code: 'resigned', label: 'Παραιτήθηκε' },
  { code: 'fired',    label: 'Απολύθηκε' },
];

export default function DoctorProfilePage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);

  /* RECEIPTS_ADMIN_CARD_2026-07-03 */
  // Receipts feature (admin).
  const [receiptMode, setReceiptMode] = useState(null);
  const [receiptSummary, setReceiptSummary] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [receiptToggleOpen, setReceiptToggleOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showCommModal, setShowCommModal] = useState(false);
  const [showIbanModal, setShowIbanModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMedicalAssocModal, setShowMedicalAssocModal] = useState(false);
  
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [canForceActivate, setCanForceActivate] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [statusForm, setStatusForm] = useState({ status: '', reason: '', notes: '' });
  const [complaintForm, setComplaintForm] = useState({ incidentDate: '', description: '', severity: 'yellow' });
  const [commForm, setCommForm] = useState({ communicationType: 'call', subject: '', notes: '', communicationDate: '' });
  const [ibanForm, setIbanForm] = useState('');
  const [medicalAssocForm, setMedicalAssocForm] = useState({ medicalAssociation: '', medicalLicenseNumber: '' });
  /* BILLING_DETAILS_UI_2026-07-06 */
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingForm, setBillingForm] = useState({
    taxId: '', addressLine: '', city: '', postalCode: '', country: 'GR', taxOffice: ''
  });
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    specialty: '', specialtyCategory: '', bio: '', languages: '', consultationFee: '', commissionEuros: '' /* @specialty-category-edit */ /* @m2adminui-commission-state */
  });

  const loadReceiptSummary = async () => {
    try {
      const { data } = await doctorProfileAPI.getReceiptsSummary(doctorId);
      const d = data?.data;
      if (!d) return;
      setReceiptMode(d.doctor?.receipt_mode ?? d.doctor?.receiptMode ?? null);
      setReceiptSummary(d.summary || null);
    } catch (err) {
      console.warn('Receipts summary fetch failed (non-fatal):', err);
    }
  };

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (doctorId) loadReceiptSummary(); }, [doctorId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await doctorProfileAPI.getFullProfile(doctorId);
      setDoctor(data.data);
      setStatusForm({ status: (data.data.isLeaver || data.data.is_leaver) ? 'leaver' : 'active', reason: '', notes: '' });
      setIbanForm(data.data.iban || '');
      setMedicalAssocForm({
        medicalAssociation: data.data.medicalAssociation || '',
        medicalLicenseNumber: data.data.medicalLicenseNumber || ''
      });
      // Hydrate billing form from doctor payload
      setBillingForm({
        taxId:       data.data.tax_id       || data.data.taxId       || '',
        taxOffice:   data.data.tax_office   || data.data.taxOffice   || '',
        addressLine: data.data.address_line || data.data.addressLine || '',
        city:        data.data.city         || '',
        postalCode:  data.data.postal_code  || data.data.postalCode  || '',
        country:     data.data.country      || 'GR',
      });
      setEditForm({
        firstName: data.data.firstName || '',
        lastName: data.data.lastName || '',
        email: data.data.email || '',
        phone: data.data.phone || '',
        specialty: data.data.specialty || '',
        specialtyCategory: data.data.specialtyCategory || data.data.specialty_category || '', /* @specialty-category-edit */
        bio: data.data.bio || '',
        languages: Array.isArray(data.data.languages) ? data.data.languages.join(', ') : (data.data.languages || ''),
        consultationFee: data.data.consultationFee || '',
        commissionEuros: (() => { /* @m2adminui-commission-load */
          const cents = data.data.commissionCents ?? data.data.commission_cents;
          return cents != null ? (Number(cents) / 100).toFixed(2) : '';
        })()
      });
    } catch (e) {
      toast.error('Αδυναμία φόρτωσης προφίλ');
      navigate('/doctors');
    } finally {
      setLoading(false);
    }
  };

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [doctorId]);

  const handleActivate = async (force = false) => {
    setActivateLoading(true);
    try {
      const { data } = await doctorProfileAPI.activate(doctorId, force);
      toast.success('✅ ' + (data?.data?.message || 'Ο ιατρός ενεργοποιήθηκε. Στάλθηκε email με σύνδεσμο.'), { duration: 6000 });
      setShowMissingModal(false);
      setCanForceActivate(false);
      load();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === 'INCOMPLETE_PROFILE' && Array.isArray(data.missing)) {
        // Functional fields missing — must be fixed, cannot bypass.
        setMissingFields(data.missing);
        setCanForceActivate(false);
        setShowMissingModal(true);
      } else if (data?.code === 'MISSING_DOCUMENTS' && Array.isArray(data.missing)) {
        // Only documents missing — admin may bypass (force) to activate now.
        setMissingFields(data.missing);
        setCanForceActivate(true);
        setShowMissingModal(true);
      } else if (data?.error) {
        toast.error(data.error);
      } else {
        toast.error('Σφάλμα ενεργοποίησης');
      }
    } finally {
      setActivateLoading(false);
    }
  };

  const handleResendSetup = async () => {
    if (!window.confirm('Επαναποστολή του email με τον σύνδεσμο ορισμού κωδικού;')) return;
    setResendLoading(true);
    try {
      const { data } = await doctorProfileAPI.resendSetup(doctorId);
      toast.success('✅ ' + (data?.data?.message || 'Email στάλθηκε ξανά.'), { duration: 5000 });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === 'NOT_ACTIVATED') {
        toast.error('Ο ιατρός δεν έχει ενεργοποιηθεί ακόμα. Πατήστε πρώτα "Ενεργοποίηση".', { duration: 5000 });
      } else if (data?.code === 'PASSWORD_ALREADY_SET') {
        toast.error('Ο ιατρός έχει ήδη ορίσει κωδικό. Χρησιμοποιήστε επαναφορά κωδικού αντ\' αυτού.', { duration: 6000 });
      } else if (data?.error) {
        toast.error(data.error);
      } else {
        toast.error('Σφάλμα αποστολής');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      const isLeaver = doctor?.isLeaver || doctor?.is_leaver;
      const target = statusForm.status;

      if (target === 'leaver' && !isLeaver) {
        // Activating leaver state — preset code is required, notes optional
        if (!statusForm.reason) {
          toast.error('Επιλέξτε λόγο αποχώρησης');
          return;
        }
        const trimmedNotes = (statusForm.notes || '').trim();
        const combinedReason = trimmedNotes
          ? `${statusForm.reason}: ${trimmedNotes}`
          : statusForm.reason;
        await doctorProfileAPI.markLeaver(doctorId, { reason: combinedReason });
        toast.success('✅ Ο ιατρός ορίστηκε ως αποχωρήσας');
      } else if (target === 'active' && isLeaver) {
        // Reactivating — notes-only free text, saved to audit log
        const trimmedNotes = (statusForm.notes || '').trim() || null;
        await doctorProfileAPI.reactivate(doctorId, { reason: trimmedNotes });
        toast.success('✅ Ο ιατρός επανενεργοποιήθηκε');
      } else {
        // No state change — keep legacy endpoint for status_reason updates etc.
        await doctorProfileAPI.updateStatus(doctorId, statusForm);
        toast.success('✅ Κατάσταση ενημερώθηκε');
      }

      setShowStatusModal(false);
      load();
    } catch (err) {
      // Backend returns 409 with appointmentCount when admin tries to mark
      // a doctor as leaver but the doctor has upcoming appointments.
      const data = err?.response?.data;
      if (data?.code === 'HAS_UPCOMING_APPOINTMENTS') {
        toast.error(`Ο ιατρός έχει ${data.appointmentCount} επερχόμενα ραντεβού. Ακυρώστε τα πρώτα.`, { duration: 6000 });
      } else if (data?.error) {
        toast.error(data.error);
      } else {
        toast.error('Σφάλμα');
      }
    }
  };

  const handleIbanUpdate = async () => {
    try {
      await doctorProfileAPI.updateIban(doctorId, ibanForm);
      toast.success('✅ IBAN ενημερώθηκε');
      setShowIbanModal(false);
      load();
    } catch { toast.error('Σφάλμα'); }
  };

  const handleMedicalAssocUpdate = async () => {
    try {
      await doctorProfileAPI.updateMedicalAssociation(doctorId, {
        medicalAssociation: medicalAssocForm.medicalAssociation || null,
        medicalLicenseNumber: medicalAssocForm.medicalLicenseNumber || null
      });
      toast.success('✅ Στοιχεία Συλλόγου ενημερώθηκαν');
      setShowMedicalAssocModal(false);
      load();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error) {
        toast.error(data.error);
      } else {
        toast.error('Σφάλμα');
      }
    }
  };

  const handleBillingUpdate = async () => {
    try {
      await doctorProfileAPI.updateBillingDetails(doctorId, {
        taxId:       billingForm.taxId       || null,
        addressLine: billingForm.addressLine || null,
        city:        billingForm.city        || null,
        postalCode:  billingForm.postalCode  || null,
        country:     billingForm.country     || 'GR',
        taxOffice:   billingForm.taxOffice   || null,
      });
      toast.success('✅ Στοιχεία τιμολόγησης ενημερώθηκαν');
      setShowBillingModal(false);
      load();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error) { toast.error(data.error); }
      else { toast.error('Σφάλμα'); }
    }
  };

  const handleProfileEdit = async () => {
    try {
      const payload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        specialty: editForm.specialty,
        specialtyCategory: editForm.specialtyCategory || undefined, /* @specialty-category-edit */
        bio: editForm.bio,
        languages: editForm.languages.split(',').map(s => s.trim()).filter(Boolean),
        consultationFee: editForm.consultationFee ? parseFloat(editForm.consultationFee) : null,
        commissionCents: editForm.commissionEuros !== '' /* @m2adminui-commission-save */
          ? Math.round(parseFloat(editForm.commissionEuros) * 100)
          : null
      };
      await doctorProfileAPI.updateProfileInfo(doctorId, payload);
      toast.success('✅ Προφίλ ενημερώθηκε');
      setShowEditModal(false);
      load();
    } catch (e) {
      console.error(e);
      toast.error('Σφάλμα κατά την ενημέρωση');
    }
  };

  // The DocumentUploadModal handles the actual upload itself; we only refresh the page on success.
  const handleDocumentUploadSuccess = () => {
    load();
  };

  const handleDeleteDocument = async (doc) => {
    if (!doc || !doc.id) return;
    const label = DOCUMENT_TYPES.find(dt => dt.key === doc.document_type)?.label || 'το έγγραφο';
    const confirmed = window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε ${label};\nΑυτή η ενέργεια δεν αναιρείται.`);
    if (!confirmed) return;
    try {
      await doctorProfileAPI.deleteDocument(doctorId, doc.id);
      toast.success('✅ Έγγραφο διαγράφηκε');
      load();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Αποτυχία διαγραφής';
      toast.error(msg);
    }
  };
  // @doc-view: fetch file bytes through the authed proxy, open in a new tab, then revoke the URL.
  const [viewingDocId, setViewingDocId] = useState(null);
  const handleViewDocument = async (doc) => {
    if (!doc || !doc.id) return;
    setViewingDocId(doc.id);
    try {
      const res = await doctorProfileAPI.getDocumentBlob(doctorId, doc.id);
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Ο περιηγητής έκλεισε το παράθυρο. Επιτρέψτε αναδυόμενα παράθυρα.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Αποτυχία ανάκτησης αρχείου';
      toast.error(msg);
    } finally {
      setViewingDocId(null);
    }
  };

  const handleAddComplaint = async () => {
    try {
      await doctorProfileAPI.addComplaint(doctorId, complaintForm);
      toast.success('✅ Παράπονο καταχωρήθηκε');
      setShowComplaintModal(false);
      setComplaintForm({ incidentDate: '', description: '', severity: 'yellow' });
      load();
    } catch { toast.error('Σφάλμα'); }
  };

  const handleUpdateComplaint = async (complaintId, updates) => {
    try {
      await doctorProfileAPI.updateComplaint(doctorId, complaintId, updates);
      toast.success('✅ Παράπονο ενημερώθηκε');
      load();
    } catch { toast.error('Σφάλμα'); }
  };

  const handleAddCommunication = async () => {
    try {
      await doctorProfileAPI.addCommunication(doctorId, commForm);
      toast.success('✅ Επικοινωνία καταχωρήθηκε');
      setShowCommModal(false);
      setCommForm({ communicationType: 'call', subject: '', notes: '', communicationDate: '' });
      load();
    } catch { toast.error('Σφάλμα'); }
  };

  const getDocStatus = (docType) => {
    const doc = doctor?.documents?.find(d => d.document_type === docType);
    if (!doc) return { status: 'missing', label: '❌ Λείπει', color: 'var(--danger)' };
    const docConfig = DOCUMENT_TYPES.find(dt => dt.key === docType);
    if (docConfig?.hasExpiry && doc.expiration_date) {
      const days = Math.ceil((new Date(doc.expiration_date) - new Date()) / (1000 * 60 * 60 * 24));
      if (days < 0) return { status: 'expired', label: '🔴 Έληξε', color: 'var(--danger)', doc };
      if (days <= 30) return { status: 'expiring', label: `⚠️ Λήγει σε ${days} μέρες`, color: 'var(--warning)', doc };
    }
    return { status: 'ok', label: '✅ Υποβλήθηκε', color: 'var(--success)', doc };
  };

  const StatusBadge = ({ status }) => {
    const colors = { active: 'var(--success)', suspended: 'var(--danger)', on_leave: 'var(--warning)' };
    const labels = { active: 'Ενεργός', suspended: 'Σε αναστολή', on_leave: 'Σε άδεια' };
    return <span style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${colors[status]}20`, color: colors[status] }}>{labels[status]}</span>;
  };

  const SeverityBadge = ({ severity }) => {
    const colors = { red: '#DC2626', yellow: '#F59E0B', green: '#10B981' };
    const labels = { red: 'Σοβαρό', yellow: 'Μέτριο', green: 'Ήπιο' };
    return <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${colors[severity]}20`, color: colors[severity] }}>{labels[severity]}</span>;
  };

  const ComplaintStatusBadge = ({ status }) => {
    const colors = { open: 'var(--danger)', under_review: 'var(--warning)', resolved: 'var(--success)' };
    const labels = { open: 'Ανοιχτό', under_review: 'Υπό εξέταση', resolved: 'Επιλύθηκε' };
    return <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${colors[status]}20`, color: colors[status] }}>{labels[status]}</span>;
  };

  if (loading) return <div className={styles.page}><div className={styles.loading}>Φόρτωση...</div></div>;
  if (!doctor) return null;

  const displayFirstName = doctor.firstName || '';
  const displayLastName = doctor.lastName || '';
  const hasName = displayFirstName || displayLastName;

  const tabs = [
    { key: 'overview', label: '📊 Επισκόπηση' },
    { key: 'documents', label: '📄 Έγγραφα' },
    { key: 'clinics', label: '🏥 Ιατρεία' }, /* @clinic-pairings-tab-entry */
    { key: 'consultations', label: '🩺 Διαβουλεύσεις' },
    { key: 'complaints', label: '⚠️ Παράπονα' },
    { key: 'communications', label: '📞 Επικοινωνία' },
    { key: 'earnings', label: '💰 Έσοδα' },
    { key: 'clearings', label: '💸 Εκκαθαρίσεις' }, /* @d10-clearings-tab-entry */
  ];

  const handleReceiptModeToggle = async () => {
    if (receiptBusy) return;
    const target = receiptMode === 'docnow' ? 'self' : 'docnow';
    setReceiptBusy(true);
    try {
      await doctorProfileAPI.setReceiptMode(doctorId, target);
      toast.success(target === 'docnow'
        ? 'Η έκδοση ΑΠΥ μέσω DocNow ενεργοποιήθηκε.'
        : 'Η έκδοση ΑΠΥ μέσω DocNow απενεργοποιήθηκε.');
      setReceiptToggleOpen(false);
      await loadReceiptSummary();
    } catch (err) {
      console.error('Receipt mode flip error:', err);
      const code = err.response?.data?.code;
      const outstanding = err.response?.data?.outstandingCount;
      if (code === 'PENDING_RECEIPTS') {
        toast.error(`Ο ιατρός έχει ${outstanding || 'ορισμένες'} εκκρεμείς ΑΠΥ. Επιλύστε τις πρώτα πριν την απενεργοποίηση.`);
      } else {
        toast.error(err.response?.data?.error || 'Σφάλμα κατά την αλλαγή');
      }
    } finally {
      setReceiptBusy(false);
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader 
        title={hasName ? `Δρ. ${displayFirstName} ${displayLastName}` : 'Προφίλ Ιατρού'} 
        subtitle={doctor.specialty || 'Γενικός Ιατρός'}
        action={<ActionBtn onClick={() => navigate('/doctors')}>← Πίσω στους Ιατρούς</ActionBtn>}
      />

      <div style={{ background: 'white', borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid var(--border)', display: 'flex', gap: 24, alignItems: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--crimson)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 28, fontWeight: 700 }}>
          {(displayFirstName[0] || '') + (displayLastName[0] || '') || '?'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>
            {hasName ? `Δρ. ${displayFirstName} ${displayLastName}` : <span style={{ color: 'var(--danger)' }}>⚠️ Χωρίς όνομα — κάντε επεξεργασία</span>}
          </div>
          <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span>✉️ {doctor.email}</span>
            {doctor.phone ? <span>📞 {doctor.phone}</span> : <span style={{ color: 'var(--danger)' }}>📞 Χωρίς τηλέφωνο</span>}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusBadge status={doctor.status || 'active'} />
            <ActionBtn variant="primary" onClick={() => setShowEditModal(true)}>✏️ Επεξεργασία Προφίλ</ActionBtn>
            <ActionBtn onClick={() => setShowStatusModal(true)}>Αλλαγή Κατάστασης</ActionBtn>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Βαθμολογία</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--warning)' }}>★ {doctor.ratingsOverview?.avgRating || '-'}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{doctor.ratingsOverview?.totalRatings || 0} αξιολογήσεις</div>
        </div>
      </div>

      {/* Onboarding banner — shown when doctor needs to be activated */}
      {!doctor.isApproved && !(doctor.isLeaver || doctor.is_leaver) && (
        <div style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FED7AA 100%)', border: '1px solid #F59E0B', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>📋 Σε φάση Onboarding</div>
              <div style={{ fontSize: 13, color: '#78350F', lineHeight: 1.5 }}>
                Ο ιατρός δεν έχει ενεργοποιηθεί. Συμπληρώστε όλα τα στοιχεία και ανεβάστε τα έγγραφα, και μετά πατήστε <strong>"Ενεργοποίηση"</strong>. Θα σταλεί email στον ιατρό με σύνδεσμο για να ορίσει κωδικό πρόσβασης.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <ActionBtn
                variant="primary"
                onClick={() => handleActivate()}
                disabled={activateLoading}
              >
                {activateLoading ? 'Ενεργοποίηση...' : '✅ Ενεργοποίηση Ιατρού'}
              </ActionBtn>
            </div>
          </div>
        </div>
      )}

      {/* Resend-setup banner — shown for activated doctors who haven't set their password yet.
          We can't tell from getFullProfile whether password_hash is null, but we CAN tell from
          isVerified (set true after successful setup). If approved + not verified, password
          probably hasn't been set. */}
      {doctor.isApproved && !(doctor.isLeaver || doctor.is_leaver) && doctor.isVerified === false && (
        <div style={{ background: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1E40AF', marginBottom: 4 }}>📧 Αναμονή ορισμού κωδικού</div>
              <div style={{ fontSize: 12, color: '#1E3A8A' }}>
                Ο ιατρός είναι ενεργοποιημένος αλλά δεν έχει ορίσει κωδικό ακόμα. Αν χρειαστεί, στείλτε ξανά το email.
              </div>
            </div>
            <ActionBtn onClick={handleResendSetup} disabled={resendLoading}>
              {resendLoading ? 'Αποστολή...' : '📧 Επαναποστολή Email'}
            </ActionBtn>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid var(--border)', paddingBottom: 12 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: '10px 16px', border: 'none', background: activeTab === t.key ? 'var(--crimson)' : 'transparent', color: activeTab === t.key ? 'white' : 'var(--slate)', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
          <Card title="📊 Στατιστικά">
            <div style={{ padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}><span>Συνολικές Διαβουλεύσεις</span><strong>{doctor.consultations?.length || 0}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}><span>Μ.Ο. Βαθμολογία</span><strong style={{ color: 'var(--warning)' }}>★ {doctor.ratingsOverview?.avgRating || '-'}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}><span>NPS Score</span><strong>{doctor.ratingsOverview?.avgNps || '-'}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}><span>Ακυρώσεις (τελευταία στιγμή)</span><strong style={{ color: 'var(--danger)' }}>{doctor.cancellationStats?.lastMinute || 0}</strong></div>
            </div>
          </Card>
          <Card title="📄 Κατάσταση Εγγράφων">
            <div style={{ padding: 10 }}>
              {DOCUMENT_TYPES.map(dt => {
                const { label, color } = getDocStatus(dt.key);
                return <div key={dt.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}><span>{dt.label}</span><span style={{ color, fontWeight: 600 }}>{label}</span></div>;
              })}
            </div>
          </Card>
          <Card title="💰 Έσοδα">
            <div style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Σύνολο</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--navy)' }}>{fmtEuro(doctor.earnings?.total)}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>Αυτό τον μήνα: {fmtEuro(doctor.earnings?.thisMonth)}</div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'documents' && (
        <>
        <Card title="💳 Στοιχεία Τιμολόγησης" action={<ActionBtn variant="primary" onClick={() => setShowBillingModal(true)}>✒️ Επεξεργασία</ActionBtn>}>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
            <div><strong>ΑΦΜ:</strong> {doctor.tax_id || doctor.taxId || <span style={{ color: 'var(--danger)' }}>—</span>}</div>
            <div><strong>Χώρα:</strong> {doctor.country || 'GR'}</div>
            <div style={{ gridColumn: '1 / -1' }}><strong>Διεύθυνση:</strong> {doctor.address_line || doctor.addressLine || <span style={{ color: 'var(--danger)' }}>—</span>}</div>
            <div><strong>Πόλη:</strong> {doctor.city || <span style={{ color: 'var(--danger)' }}>—</span>}</div>
            <div><strong>Τ.Κ.:</strong> {doctor.postal_code || doctor.postalCode || <span style={{ color: 'var(--danger)' }}>—</span>}</div>
            {/* @tax-office-ui-21jul */}
            <div style={{ gridColumn: '1 / -1' }}><strong>ΔΟΥ:</strong> {doctor.tax_office || doctor.taxOffice || <span style={{ color: 'var(--danger)' }}>—</span>}</div>
          </div>
        </Card>

        <Card title="🧾 Έκδοση ΑΠΥ">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: '#8A97A8' }}>Τρέχουσα λειτουργία:</span>
              <span style={{
                fontSize: 12, padding: '4px 10px', borderRadius: 6, fontWeight: 600,
                background: receiptMode === 'docnow' ? '#E7F6EF' : '#F3F4F6',
                color:      receiptMode === 'docnow' ? '#0D9A6A' : '#374151',
              }}>
                {receiptMode === 'docnow' ? 'Μέσω DocNow' : (receiptMode === 'self' ? 'Αυτο-έκδοση' : '—')}
              </span>
              <ActionBtn
                variant="primary"
                onClick={() => setReceiptToggleOpen(true)}
                disabled={receiptBusy || receiptMode === null}
              >
                {receiptMode === 'docnow' ? 'Απενεργοποίηση DocNow' : 'Ενεργοποίηση DocNow'}
              </ActionBtn>
            </div>
            {receiptSummary && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: '#0F1F3D' }}>
                <div>Εκδόθηκαν: <strong>{receiptSummary.apy_issued ?? 0}</strong></div>
                <div>Εκκρεμούν: <strong>{receiptSummary.apy_pending ?? 0}</strong></div>
                <div>Απέτυχαν: <strong>{receiptSummary.apy_failed ?? 0}</strong></div>
                <div>Ακυρώθηκαν: <strong>{receiptSummary.apy_cancelled ?? 0}</strong></div>
                <div>Ακυρωτικά: <strong>{receiptSummary.akyrotico_issued ?? 0}</strong></div>
              </div>
            )}
          </div>
        </Card>

        <Card title="📄 Έγγραφα" action={<ActionBtn variant="primary" onClick={() => setShowDocModal(true)}>+ Προσθήκη Εγγράφου</ActionBtn>}>
          <div style={{ padding: 16 }}>
            <div style={{ padding: 16, background: 'var(--bg)', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><strong>IBAN:</strong> {doctor.iban || <span style={{ color: 'var(--danger)' }}>Δεν έχει καταχωρηθεί</span>}</div>
              <ActionBtn onClick={() => setShowIbanModal(true)}>Επεξεργασία</ActionBtn>
            </div>
            {DOCUMENT_TYPES.map(dt => {
              const { label, color, doc } = getDocStatus(dt.key);
              const docRow = (
                <div key={dt.key} style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {doc && doc.id && (
                      <DocumentImagePreview
                        doctorId={doctorId}
                        docId={doc.id}
                        mimeType={doc.mime_type}
                        size={56}
                        onClick={() => handleViewDocument(doc)}
                      />
                    )}
                    <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{dt.label}</div>
                    {doc && <div style={{ fontSize: 12, color: 'var(--muted)' }}>Αρχείο: {doc.file_name || 'N/A'}</div>}
                    {doc?.expiration_date && <div style={{ fontSize: 12, color: 'var(--muted)' }}>Λήξη: {new Date(doc.expiration_date).toLocaleDateString('el-GR')}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color, fontWeight: 600 }}>{label}</span>
                    {doc && (
                      <button
                        onClick={() => handleViewDocument(doc)}
                        disabled={viewingDocId === doc.id}
                        title="Προβολή εγγράφου"
                        aria-label="Προβολή εγγράφου"
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: viewingDocId === doc.id ? 'wait' : 'pointer',
                          fontSize: 14,
                          lineHeight: 1,
                          marginRight: 6,
                        }}
                      >
                        {viewingDocId === doc.id ? '⏳' : '👁 Προβολή'}
                      </button>
                    )}
                    {doc && (
                      <button
                        onClick={() => handleDeleteDocument(doc)}
                        title="Διαγραφή εγγράφου"
                        aria-label="Διαγραφή εγγράφου"
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: 14,
                          lineHeight: 1,
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              );
              if (dt.key === 'medical_association_cert') {
                return (
                  <React.Fragment key={dt.key}>
                    {docRow}
                    <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12, background: 'var(--bg)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ fontWeight: 600 }}>📋 Στοιχεία Εγγραφής Συλλόγου</div>
                        <ActionBtn onClick={() => setShowMedicalAssocModal(true)}>Επεξεργασία</ActionBtn>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ιατρικός Σύλλογος</div>
                          <div style={{ fontSize: 14 }}>
                            {doctor.medicalAssociation || <span style={{ color: 'var(--danger)' }}>Δεν έχει καταχωρηθεί</span>}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Αριθμός Μητρώου</div>
                          <div style={{ fontSize: 14 }}>
                            {doctor.medicalLicenseNumber || <span style={{ color: 'var(--danger)' }}>Δεν έχει καταχωρηθεί</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              }
              return docRow;
            })}
          </div>
        </Card>
        </>
      )}

      {activeTab === 'consultations' && (
        <Card title="🩺 Ιστορικό Διαβουλεύσεων">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: 12 }}>Ημερομηνία</th>
                  <th style={{ textAlign: 'left', padding: 12 }}>Ασθενής</th>
                  <th style={{ textAlign: 'center', padding: 12 }}>Κατάσταση</th>
                  <th style={{ textAlign: 'left', padding: 12 }}>Έκβαση</th>
                  <th style={{ textAlign: 'center', padding: 12 }}>Βαθμολογία</th>
                  <th style={{ textAlign: 'left', padding: 12 }}>Σχόλιο</th>
                </tr>
              </thead>
              <tbody>
                {(doctor.consultations || []).length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>Δεν υπάρχουν διαβουλεύσεις</td></tr>
                ) : (
                  (doctor.consultations || []).map((c, i) => {
                    // Translate completion status to Greek; fall through to raw value
                    // for any state we haven't mapped (defensive — shouldn't happen).
                    const statusLabel = COMPLETION_STATUS_LABELS_EL[c.status] || c.status;
                    // Clinical outcome only present for status='completed' rows.
                    // Backend may send it as clinicalOutcome (camelCase) or
                    // clinical_outcome (snake_case) depending on mapping — accept either.
                    const clinicalOutcome = c.clinicalOutcome || c.clinical_outcome || null;
                    const clinicalDetails = c.clinicalOutcomeDetails || c.clinical_outcome_details || null;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: 12 }}>{new Date(c.date).toLocaleDateString('el-GR')} {c.startTime?.slice(0,5)}</td>
                        <td style={{ padding: 12 }}>{c.patientName}</td>
                        <td style={{ textAlign: 'center', padding: 12 }}>
                          <span style={{
                            padding: '4px 8px', borderRadius: 8, fontSize: 11,
                            background: c.status === 'completed' ? 'var(--success-light)' : 'var(--bg)',
                            color: c.status === 'completed' ? 'var(--success)' : 'var(--muted)'
                          }}>{statusLabel}</span>
                        </td>
                        <td style={{ padding: 12, maxWidth: 220 }}>
                          {clinicalOutcome ? (
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--navy)' }}>
                                {CLINICAL_OUTCOME_LABELS_EL[clinicalOutcome] || clinicalOutcome}
                              </div>
                              {clinicalDetails && (
                                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, fontStyle: 'italic' }}>
                                  {clinicalDetails}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--muted)' }}>—</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', padding: 12 }}>{c.rating ? <span style={{ color: 'var(--warning)' }}>★ {c.rating}</span> : '-'}</td>
                        <td style={{ padding: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.comment || '-'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {activeTab === 'complaints' && (
        <Card title="⚠️ Παράπονα" action={<ActionBtn variant="primary" onClick={() => setShowComplaintModal(true)}>+ Νέο Παράπονο</ActionBtn>}>
          <div style={{ padding: 16 }}>
            {(doctor.complaints || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Δεν υπάρχουν παράπονα</div>
            ) : (
              (doctor.complaints || []).map((c, i) => (
                <div key={i} style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <SeverityBadge severity={c.severity} />
                      <ComplaintStatusBadge status={c.status} />
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date(c.incidentDate).toLocaleDateString('el-GR')}</span>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 8 }}>{c.description}</p>
                  {c.patientName && <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ασθενής: {c.patientName}</div>}
                  {c.outcome && <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 8 }}>Έκβαση: {c.outcome}</div>}
                  {c.status !== 'resolved' && (
                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                      <ActionBtn onClick={() => handleUpdateComplaint(c.id, { status: 'under_review' })}>Υπό Εξέταση</ActionBtn>
                      <ActionBtn variant="primary" onClick={() => {
                        const outcome = prompt('Έκβαση παραπόνου:');
                        if (outcome) handleUpdateComplaint(c.id, { status: 'resolved', outcome, resolvedDate: new Date().toISOString().split('T')[0] });
                      }}>Επίλυση</ActionBtn>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {activeTab === 'communications' && (
        <Card title="📞 Ημερολόγιο Επικοινωνίας" action={<ActionBtn variant="primary" onClick={() => setShowCommModal(true)}>+ Νέα Καταχώρηση</ActionBtn>}>
          <div style={{ padding: 16 }}>
            {(doctor.communications || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Δεν υπάρχουν καταχωρήσεις</div>
            ) : (
              (doctor.communications || []).map((c, i) => (
                <div key={i} style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--bg)', color: 'var(--navy)' }}>
                      {c.communication_type === 'call' ? '📞 Κλήση' : c.communication_type === 'email' ? '📧 Email' : c.communication_type === 'meeting' ? '🤝 Συνάντηση' : '📝 Άλλο'}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date(c.communication_date).toLocaleDateString('el-GR')}</span>
                  </div>
                  {c.subject && <div style={{ fontWeight: 600, marginBottom: 4 }}>{c.subject}</div>}
                  <p style={{ fontSize: 13, color: 'var(--slate)' }}>{c.notes}</p>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {activeTab === 'earnings' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <Card title="💰 Σύνοψη Εσόδων">
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
                <span>Συνολικά Έσοδα</span>
                <strong style={{ fontSize: 20, color: 'var(--navy)' }}>{fmtEuro(doctor.earnings?.total)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0' }}>
                <span>Αυτό τον Μήνα</span>
                <strong style={{ fontSize: 20, color: 'var(--success)' }}>{fmtEuro(doctor.earnings?.thisMonth)}</strong>
              </div>
            </div>
          </Card>
          <Card title="📈 Ακυρώσεις">
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <span>Συνολικές Ακυρώσεις</span>
                <strong>{doctor.cancellationStats?.total || 0}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <span>Τελευταία Στιγμή (&lt;24ώρες)</span>
                <strong style={{ color: 'var(--danger)' }}>{doctor.cancellationStats?.lastMinute || 0}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                <span>Ίδια Μέρα</span>
                <strong style={{ color: 'var(--warning)' }}>{doctor.cancellationStats?.sameDay || 0}</strong>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* @d10-clearings-render */}
      {activeTab === 'clinics' && (
        /* @clinic-pairings-render */
        <DoctorClinicsTab doctorId={doctorId} />
      )}

      {activeTab === 'clearings' && (
        <DoctorMonthlyClearingsTab doctorId={doctorId} />
      )}

      {/* EDIT PROFILE MODAL */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)} title="Επεξεργασία Προφίλ Ιατρού" size="md">
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Όνομα</label>
              <input value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Επώνυμο</label>
              <input value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Email</label>
            <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Τηλέφωνο</label>
            <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="π.χ. 6912345678" />
          </div>
          <div style={{ marginBottom: 12 }}>
            {/* @dropdown-specialty */}
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Ειδικότητα</label>
            {/* @specialty-category-edit: keep specialty_category in step with specialty, same map as Doctors.jsx create form */}
            <select value={editForm.specialty} onChange={e => {
              const v = e.target.value;
              const SPECIALTY_TO_CATEGORY = {
                'Γενικός / Οικογενειακός Ιατρός': 'general_practitioner',
                'Παθολόγος': 'specialist',
                'Ιατρός': 'general_practitioner',
              };
              setEditForm({ ...editForm, specialty: v, specialtyCategory: SPECIALTY_TO_CATEGORY[v] || editForm.specialtyCategory });
            }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }}>
              <option value="">— Επιλέξτε ειδικότητα —</option>
              <option value="Γενικός / Οικογενειακός Ιατρός">Γενικός / Οικογενειακός Ιατρός</option>
              <option value="Παθολόγος">Παθολόγος</option>
              <option value="Ιατρός">Ιατρός</option>
              {editForm.specialty && editForm.specialty !== 'Γενικός / Οικογενειακός Ιατρός' && editForm.specialty !== 'Παθολόγος' && editForm.specialty !== 'Ιατρός' && (
                <option value={editForm.specialty}>{editForm.specialty}</option>
              )}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            {/* @dropdown-languages */}
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Γλώσσες</label>
            {(() => {
              const LANGS = [['el','🇬🇷'],['en','🇬🇧'],['fr','🇫🇷'],['es','🇪🇸'],['de','🇩🇪'],['it','🇮🇹'],['pt','🇵🇹'],['ar','🇸🇦'],['ru','🇷🇺'],['tr','🇹🇷']];
              const selected = editForm.languages.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
              const toggle = (code) => {
                const set = new Set(selected);
                if (set.has(code)) set.delete(code); else set.add(code);
                const ordered = LANGS.filter(([c]) => set.has(c)).map(([c]) => c);
                setEditForm({ ...editForm, languages: ordered.join(', ') });
              };
              return (
                <details style={{ width: '100%' }}>
                  <summary style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: 10, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', background: '#fff', boxSizing: 'border-box' }}>
                    {selected.length === 0
                      ? <span style={{ color: 'var(--muted)' }}>Επιλέξτε γλώσσες…</span>
                      : <span>{LANGS.filter(([c]) => selected.includes(c)).map(([c, f]) => f + ' ' + c.toUpperCase()).join('   ')}</span>}
                    <span style={{ marginLeft: 'auto', color: 'var(--muted)' }}>▾</span>
                  </summary>
                  <div style={{ marginTop: 6, border: '1px solid var(--border)', borderRadius: 8, padding: 6, maxHeight: 240, overflowY: 'auto' }}>
                    {LANGS.map(([code, flag]) => {
                      const on = selected.includes(code);
                      return (
                        <div key={code} onClick={() => toggle(code)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', background: on ? 'var(--bg)' : 'transparent' }}>
                          <input type="checkbox" checked={on} readOnly style={{ pointerEvents: 'none' }} />
                          <span style={{ fontSize: 16 }}>{flag}</span>
                          <span style={{ fontWeight: 600 }}>{code.toUpperCase()}</span>
                        </div>
                      );
                    })}
                  </div>
                </details>
              );
            })()}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Αμοιβή Διαβούλευσης (€)</label>
            <input type="number" step="0.01" value={editForm.consultationFee} onChange={e => setEditForm({ ...editForm, consultationFee: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} />
          </div>
          <div style={{ marginBottom: 12 }}>{/* @m2adminui-commission-input */}
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Προμήθεια DocNow (€)</label>
            <input type="number" step="0.01" min="0" max="20" value={editForm.commissionEuros} onChange={e => setEditForm({ ...editForm, commissionEuros: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="7.50" />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Συμπεριλαμβανομένου ΦΠΑ 24%. Προεπιλογή: €7.50. Επιτρεπτό εύρος: €0.00 — €20.00.</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Βιογραφικό</label>
            <textarea value={editForm.bio} onChange={e => setEditForm({ ...editForm, bio: e.target.value })} rows={4} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => setShowEditModal(false)}>Ακύρωση</ActionBtn>
            <ActionBtn variant="primary" onClick={handleProfileEdit}>💾 Αποθήκευση</ActionBtn>
          </div>
        </div>
      </Modal>

      <Modal open={showStatusModal} onClose={() => setShowStatusModal(false)} title="Αλλαγή Κατάστασης" size="sm">
        <div style={{ padding: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Κατάσταση</label>
            <select value={statusForm.status} onChange={e => setStatusForm({ ...statusForm, status: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
              <option value="active">Ενεργός</option>
              <option value="leaver">Αποχωρήσας</option>
            </select>
          </div>
          {statusForm.status === 'leaver' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Λόγος αποχώρησης *</label>
                <select value={statusForm.reason} onChange={e => setStatusForm({ ...statusForm, reason: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
                  <option value="">— Επιλέξτε —</option>
                  {LEAVER_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Σημειώσεις</label>
                <textarea value={statusForm.notes} onChange={e => setStatusForm({ ...statusForm, notes: e.target.value })} rows={3} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="Προαιρετικά: επιπλέον λεπτομέρειες..." />
              </div>
            </>
          )}
          {statusForm.status === 'active' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Σημειώσεις επανενεργοποίησης</label>
              <textarea value={statusForm.notes} onChange={e => setStatusForm({ ...statusForm, notes: e.target.value })} rows={3} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="Προαιρετικά: π.χ. Επιστρέφει μετά από άδεια άνευ αποδοχών" />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => setShowStatusModal(false)}>Ακύρωση</ActionBtn>
            <ActionBtn variant="primary" onClick={handleStatusUpdate}>Αποθήκευση</ActionBtn>
          </div>
        </div>
      </Modal>

      <Modal open={showIbanModal} onClose={() => setShowIbanModal(false)} title="Επεξεργασία IBAN" size="sm">
        <div style={{ padding: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>IBAN</label>
            <input value={ibanForm} onChange={e => setIbanForm(e.target.value)} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="GR..." />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => setShowIbanModal(false)}>Ακύρωση</ActionBtn>
            <ActionBtn variant="primary" onClick={handleIbanUpdate}>Αποθήκευση</ActionBtn>
          </div>
        </div>
      </Modal>

      <Modal open={showBillingModal} onClose={() => setShowBillingModal(false)} title="Επεξεργασία Στοιχείων Τιμολόγησης" size="md">
        {/* @billing-modal-layout-21jul */}
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>ΑΦΜ (9 ψηφία)</label>
              <input value={billingForm.taxId} onChange={(e) => setBillingForm({ ...billingForm, taxId: e.target.value })} placeholder="123456789" maxLength={9} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Χώρα</label>
              <input value={billingForm.country} onChange={(e) => setBillingForm({ ...billingForm, country: e.target.value.toUpperCase() })} placeholder="GR" maxLength={2} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Διεύθυνση</label>
            <input value={billingForm.addressLine} onChange={(e) => setBillingForm({ ...billingForm, addressLine: e.target.value })} placeholder="Οδός & αριθμός" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Πόλη</label>
              <input value={billingForm.city} onChange={(e) => setBillingForm({ ...billingForm, city: e.target.value })} placeholder="Αθήνα" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Τ.Κ.</label>
              <input value={billingForm.postalCode} onChange={(e) => setBillingForm({ ...billingForm, postalCode: e.target.value })} placeholder="10557" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>ΔΟΥ</label>
            <input value={billingForm.taxOffice} onChange={(e) => setBillingForm({ ...billingForm, taxOffice: e.target.value })} placeholder="π.χ. Χαλανδρίου" maxLength={100} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => setShowBillingModal(false)}>Άκυρο</ActionBtn>
            <ActionBtn variant="primary" onClick={handleBillingUpdate}>Αποθήκευση</ActionBtn>
          </div>
        </div>
      </Modal>

      <Modal open={receiptToggleOpen} onClose={() => { if (!receiptBusy) setReceiptToggleOpen(false); }} title="Αλλαγή λειτουργίας ΑΠΥ" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, color: '#0F1F3D', fontSize: 14, lineHeight: 1.5 }}>
            {receiptMode === 'docnow'
              ? 'Η έκδοση ΑΠΥ μέσω DocNow θα απενεργοποιηθεί. Η ενέργεια αποτυγχάνει αν υπάρχουν εκκρεμείς ΑΠΥ.'
              : 'Η έκδοση ΑΠΥ μέσω DocNow θα ενεργοποιηθεί. Ο ιατρός θα βλέπει την popup μετά το κλείσιμο μιας διαβούλευσης.'}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => { if (!receiptBusy) setReceiptToggleOpen(false); }} disabled={receiptBusy}>Άκυρο</ActionBtn>
            <ActionBtn variant="primary" onClick={handleReceiptModeToggle} disabled={receiptBusy}>
              {receiptBusy ? '...' : 'Επιβεβαίωση'}
            </ActionBtn>
          </div>
        </div>
      </Modal>

      <Modal open={showMedicalAssocModal} onClose={() => setShowMedicalAssocModal(false)} title="Στοιχεία Εγγραφής Συλλόγου" size="md">
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Ιατρικός Σύλλογος</label>
              <input value={medicalAssocForm.medicalAssociation} onChange={e => setMedicalAssocForm({ ...medicalAssocForm, medicalAssociation: e.target.value })} maxLength={100} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="π.χ. Αθηνών" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Αριθμός Μητρώου</label>
              <input value={medicalAssocForm.medicalLicenseNumber} onChange={e => setMedicalAssocForm({ ...medicalAssocForm, medicalLicenseNumber: e.target.value })} maxLength={50} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="π.χ. 12345" />
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
            Τα στοιχεία αυτά εμφανίζονται στις παραπεμπτικές επιστολές που εκδίδει ο ιατρός. Πρέπει να συμφωνούν με τη Βεβαίωση Εγγραφής του Συλλόγου.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => setShowMedicalAssocModal(false)}>Ακύρωση</ActionBtn>
            <ActionBtn variant="primary" onClick={handleMedicalAssocUpdate}>Αποθήκευση</ActionBtn>
          </div>
        </div>
      </Modal>

      <DocumentUploadModal
        open={showDocModal}
        onClose={() => setShowDocModal(false)}
        onSuccess={handleDocumentUploadSuccess}
        doctorId={doctorId}
      />

      <Modal open={showComplaintModal} onClose={() => setShowComplaintModal(false)} title="Νέο Παράπονο" size="md">
        <div style={{ padding: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Ημερομηνία Συμβάντος</label>
            <DateInput value={complaintForm.incidentDate} onChange={e => setComplaintForm({ ...complaintForm, incidentDate: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Σοβαρότητα</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['green', 'yellow', 'red'].map(s => (
                <button key={s} onClick={() => setComplaintForm({ ...complaintForm, severity: s })} style={{ flex: 1, padding: 12, borderRadius: 8, border: complaintForm.severity === s ? '2px solid var(--navy)' : '1px solid var(--border)', background: s === 'green' ? '#10B98120' : s === 'yellow' ? '#F59E0B20' : '#DC262620', color: s === 'green' ? '#10B981' : s === 'yellow' ? '#F59E0B' : '#DC2626', fontWeight: 600, cursor: 'pointer' }}>
                  {s === 'green' ? '🟢 Ήπιο' : s === 'yellow' ? '🟡 Μέτριο' : '🔴 Σοβαρό'}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Περιγραφή</label>
            <textarea value={complaintForm.description} onChange={e => setComplaintForm({ ...complaintForm, description: e.target.value })} rows={4} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="Περιγράψτε το παράπονο..." />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => setShowComplaintModal(false)}>Ακύρωση</ActionBtn>
            <ActionBtn variant="primary" onClick={handleAddComplaint} disabled={!complaintForm.incidentDate || !complaintForm.description}>Καταχώρηση</ActionBtn>
          </div>
        </div>
      </Modal>

      <Modal open={showCommModal} onClose={() => setShowCommModal(false)} title="Νέα Επικοινωνία" size="md">
        <div style={{ padding: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Τύπος</label>
            <select value={commForm.communicationType} onChange={e => setCommForm({ ...commForm, communicationType: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
              <option value="call">📞 Τηλεφωνική Κλήση</option>
              <option value="email">📧 Email</option>
              <option value="meeting">🤝 Συνάντηση</option>
              <option value="other">📝 Άλλο</option>
            </select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Ημερομηνία</label>
            <DateInput value={commForm.communicationDate} onChange={e => setCommForm({ ...commForm, communicationDate: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Θέμα</label>
            <input value={commForm.subject} onChange={e => setCommForm({ ...commForm, subject: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="π.χ. Ενημέρωση για έγγραφα" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Σημειώσεις</label>
            <textarea value={commForm.notes} onChange={e => setCommForm({ ...commForm, notes: e.target.value })} rows={4} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }} placeholder="Λεπτομέρειες επικοινωνίας..." />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <ActionBtn onClick={() => setShowCommModal(false)}>Ακύρωση</ActionBtn>
            <ActionBtn variant="primary" onClick={handleAddCommunication} disabled={!commForm.notes}>Καταχώρηση</ActionBtn>
          </div>
        </div>
      </Modal>

      {/* Missing-fields modal — shown when activate returns 422 INCOMPLETE_PROFILE */}
      <Modal open={showMissingModal} onClose={() => setShowMissingModal(false)} title={canForceActivate ? 'Λείπουν έγγραφα' : 'Δεν είναι έτοιμος για ενεργοποίηση'} size="md">
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 14, color: 'var(--slate)', marginBottom: 16, lineHeight: 1.5 }}>
            {canForceActivate
              ? 'Λείπουν τα παρακάτω έγγραφα. Μπορείτε να ενεργοποιήσετε τον ιατρό τώρα και να τα προσθέσετε αργότερα, ή να ακυρώσετε και να τα ανεβάσετε πρώτα.'
              : 'Πριν την ενεργοποίηση πρέπει να συμπληρωθούν τα παρακάτω. Όλα είναι υποχρεωτικά για να μπορεί ο ιατρός να εργαστεί νόμιμα.'}
          </div>
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: 16 }}>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#991B1B', fontSize: 13, lineHeight: 1.8 }}>
              {missingFields.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <ActionBtn onClick={() => setShowMissingModal(false)}>{canForceActivate ? 'Άκυρο' : 'Κατανοητό'}</ActionBtn>
            {canForceActivate && (
              <ActionBtn variant="primary" disabled={activateLoading} onClick={() => handleActivate(true)}>
                {activateLoading ? 'Ενεργοποίηση...' : 'Ενεργοποίηση χωρίς έγγραφα'}
              </ActionBtn>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
